import type React from 'react';
import { createContext, type ReactNode, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { orphanOverlayHistory, popOverlayHistory, pushOverlayHistory } from './overlayHistory';

// SSR-safe layout effect: useLayoutEffect on client, useEffect on server (avoids SSR warning).
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * FullscreenOverlay Context for managing open/close state
 */
type FullscreenOverlayContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  onOpenChange?: (open: boolean) => void;
  triggerRef: React.MutableRefObject<{ element: HTMLElement | null; keyboard: boolean; scrollY: number | null }>;
};

const FullscreenOverlayContext = createContext<FullscreenOverlayContextValue | null>(null);

/**
 * Hook to access FullscreenOverlay context
 */
export const useFullscreenOverlay = () => {
  const context = useContext(FullscreenOverlayContext);
  if (!context) {
    throw new Error('useFullscreenOverlay must be used within a FullscreenOverlay.Root');
  }
  return context;
};

/**
 * FullscreenOverlay Context Provider props
 */
type FullscreenOverlayProviderProps = {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * FullscreenOverlay Context Provider
 *
 * Manages the open/close state for the overlay using either controlled or uncontrolled patterns.
 * Follows React patterns similar to form inputs - supports both controlled (open prop)
 * and uncontrolled (defaultOpen prop) usage.
 *
 * On mobile, pushes a synthetic browser history entry when the overlay opens so that
 * swiping back closes the overlay instead of navigating away from the page.
 */
export const FullscreenOverlayProvider = ({
  children,
  open,
  defaultOpen = false,
  onOpenChange,
}: FullscreenOverlayProviderProps) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [triggerRef] = useState(() => ({
    current: { element: null as HTMLElement | null, keyboard: false, scrollY: null as number | null },
  }));

  // Use controlled value if provided, otherwise use internal state
  const isOpen = open !== undefined ? open : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    // Update internal state if uncontrolled
    if (open === undefined) {
      setInternalOpen(newOpen);
    }

    // Call external callback if provided
    onOpenChange?.(newOpen);
  };

  // --- Mobile browser-back history management ---
  const overlayIdRef = useRef(Math.random().toString(36).substr(2, 9));
  const historyActiveRef = useRef(false);
  const closedByBrowserBackRef = useRef(false);

  // Track isOpen in a ref so the cleanup can distinguish an intentional close
  // (isOpen changed to false) from an unmount while still open.
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  // Keep a mutable ref to the close function so the global popstate handler
  // always calls the latest version (avoids stale closure over handleOpenChange).
  const closeFnRef = useRef<() => void>(() => {});
  closeFnRef.current = () => {
    closedByBrowserBackRef.current = true;
    handleOpenChange(false);
  };

  // Layout effect so the history entry is pushed BEFORE Portal's useEffect
  // does window.scrollTo(0, 0). This preserves the correct scroll position
  // for browser scroll restoration.
  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile || !isOpen) return;

    const id = overlayIdRef.current;
    pushOverlayHistory(id, () => closeFnRef.current());
    historyActiveRef.current = true;
    closedByBrowserBackRef.current = false;

    return () => {
      if (historyActiveRef.current) {
        if (closedByBrowserBackRef.current) {
          // Browser back already popped the history entry — no action needed.
        } else if (!isOpenRef.current) {
          // isOpen changed to false → intentional close (X, ESC, backdrop,
          // or controlled parent). Pop the history entry.
          popOverlayHistory(id);
        } else {
          // Component unmounting while still open (e.g., user navigated away
          // from inside the spoke). Orphan the entry — don't call history.back()
          // or it would fight the real navigation.
          orphanOverlayHistory(id);
        }
        historyActiveRef.current = false;
        closedByBrowserBackRef.current = false;
      }
    };
  }, [isOpen]);

  const contextValue: FullscreenOverlayContextValue = {
    isOpen,
    open: () => handleOpenChange(true),
    close: () => handleOpenChange(false),
    onOpenChange: handleOpenChange,
    triggerRef,
  };

  return <FullscreenOverlayContext.Provider value={contextValue}>{children}</FullscreenOverlayContext.Provider>;
};
